import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Snackbar,
  Alert,
  Typography,
  CircularProgress,
  Stack,
  Button,
} from '@mui/material';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import Header from '../Header';
import Sidebar from './Sidebar';
import ExcelDropzone from './Dropzone';
import RowTable from './Table';

export default function Dashboard() {
  const [history, setHistory] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [lastUploaded, setLastUploaded] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));

  const fetchHistory = useCallback(async (dateIso) => {
    try {
      const base = 'https://stage-apiplatform.finkraftai.com/api/misc/bookingdata/client/history';
      const url = dateIso ? `${base}?date=${encodeURIComponent(dateIso)}` : base;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch history');
      const json = await res.json();
      const list = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
      if (!Array.isArray(list)) return;
      const sorted = [...list].sort(
        (a, b) => new Date(b.uploaded_at).getTime() - new Date(a.uploaded_at).getTime()
      );
      setHistory(sorted);
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleFilterDateChange = useCallback(
    (iso) => {
      fetchHistory(iso);
    },
    [fetchHistory]
  );

  const handleUploadComplete = (serverResponse) => {
    // After successful upload, refresh from server to reflect canonical data
    fetchHistory();
    const payload = serverResponse?.data || serverResponse;
    const fileName = payload?.file_name || payload?.name || 'File';
    const uploadedAt = payload?.uploaded_at || new Date().toISOString();
    setLastUploaded({ name: fileName, uploadedAt });
    setSnackbarOpen(true);
  };

  const handleSelectHistory = useCallback(async (file) => {
    setSelectedFile(file);
    setRowsLoading(true);
    setRows([]);
    try {
      const res = await fetch(
        `https://stage-apiplatform.finkraftai.com/api/misc/bookingdata/client/history/rows/${file?._id}`
      );
      const json = await res.json();
      const list = Array.isArray(json) ? json : Array.isArray(json?.data) ? json.data : [];
      setRows(list);
    } catch (e) {
      setRows([]);
    } finally {
      setRowsLoading(false);
    }
  }, []);

  const handleCloseTable = useCallback(() => {
    setSelectedFile(null);
    setRows([]);
  }, []);

  const mainLeftOffset = 320; // sidebar width

  const lastSummary = useMemo(() => {
    if (!lastUploaded) return null;
    return `Last uploaded: ${lastUploaded.name} at ${new Date(lastUploaded.uploadedAt).toLocaleTimeString()}`;
  }, [lastUploaded]);

  return (
    <Box>
      <Header history={history} />
      <Sidebar
        history={history}
        onSelect={handleSelectHistory}
        onFilterDateChange={handleFilterDateChange}
      />
      <Container
        maxWidth={false}
        sx={{
          ml: `${mainLeftOffset}px`,
          width: `calc(100% - ${mainLeftOffset}px)`,
          pt: '96px',
          pb: 6,
          overflowX: 'hidden',
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h6">Upload Excel</Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Box sx={{ width: 200 }}>
                <DatePicker
                  value={selectedDate ? dayjs(selectedDate) : null}
                  onChange={(d, dateString) => setSelectedDate(dateString || '')}
                  size="small"
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                  allowClear
                />
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedDate(dayjs().format('YYYY-MM-DD'))}
              >
                Today
              </Button>
            </Stack>
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Drag and drop your .xlsx or .xls file into the area below.
          </Typography>
        </Box>
        <ExcelDropzone selectedDate={selectedDate} onUploadComplete={handleUploadComplete} />
        {lastSummary && (
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            {lastSummary}
          </Typography>
        )}

        {selectedFile &&
          (rowsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Loading rows…
              </Typography>
            </Box>
          ) : (
            <RowTable file={selectedFile} rows={rows} onClose={handleCloseTable} />
          ))}
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2200}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setSnackbarOpen(false)}>
          File uploaded successfully
        </Alert>
      </Snackbar>
    </Box>
  );
}
